import React, { useContext } from 'react';
import { Heading } from 'js/design_system/Heading';
import { Body } from 'js/design_system/Body';
import { TextButton } from 'js/design_system/TextButton';
import { CiterightContext } from './CiterightContextProvider';
import ClaimCard from './components/ClaimCard';

interface OverviewPageProps {
  onClaimSelect: (claimId: string) => void;
  onSwitchToTriage?: () => void;
}

export const OverviewPage: React.FC<OverviewPageProps> = ({
  onClaimSelect,
  onSwitchToTriage,
}) => {
  const context = useContext(CiterightContext);
  const overviewData = context?.report?.overview;
  const reasoning = overviewData?.reasoning;
  const references = context?.report?.references || [];
  const reportClaims = context?.report?.claims || [];

  // Map overview claims to actual report claims that have actions
  const topClaims = (overviewData?.top_claims || []).map((overviewClaim) => {
    // Find the matching claim from the report that has actions
    const claimWithActions = reportClaims.find(
      (c) => c.id === overviewClaim.id,
    );
    return claimWithActions || overviewClaim;
  });

  if (!context?.report?.overview_ready) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <Heading size="sm" as="h2">
          Key Claims
        </Heading>
        <Body size="sm" style={{ color: 'var(--text-secondary)' }}>
          Analyzing your document's claims...
        </Body>
        {onSwitchToTriage && (
          <div style={{ marginTop: '8px', textAlign: 'center' }}>
            <TextButton onClick={onSwitchToTriage}>
              View All Claims in Triage
            </TextButton>
          </div>
        )}
      </div>
    );
  }

  if (topClaims.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <Heading size="sm" as="h2">
          Key Claims
        </Heading>
        <Body size="sm" style={{ color: 'var(--text-secondary)' }}>
          No claims found that need citation support.
        </Body>
        <div style={{ marginTop: '8px', textAlign: 'center' }}>
          <TextButton onClick={onSwitchToTriage}>
            View All Claims in Triage
          </TextButton>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 20px',
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <Heading size="lg" as="h1" style={{ marginBottom: '8px' }}>
          Key Claims
        </Heading>
        <Body size="md" style={{ color: 'var(--text-secondary)' }}>
          Here are the top {topClaims.length} claims that would benefit most
          from citation support
        </Body>
      </div>

      {reasoning && (
        <div
          style={{
            backgroundColor: 'var(--background-light-blue)',
            border: '1px solid var(--stroke-light)',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '8px',
          }}
        >
          <Heading size="sm" as="h3" style={{ marginBottom: '12px' }}>
            Claim Analysis
          </Heading>
          <Body
            size="sm"
            style={{
              color: 'var(--text-primary)',
              lineHeight: '1.6',
              whiteSpace: 'pre-line',
            }}
          >
            {reasoning}
          </Body>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {topClaims.map((claim, index: number) => {
          // For overview, show only the top suggested reference
          const overviewClaim = {
            ...claim,
            search: claim.search
              ? {
                  ...claim.search,
                  suggested_works:
                    claim.search.suggested_works?.slice(0, 1) || [],
                }
              : claim.search,
          };

          return (
            <ClaimCard
              key={claim.id}
              claim={overviewClaim}
              index={index}
              references={references}
              onClaimSelect={onClaimSelect}
              onDismiss={() => {}} // No-op for overview
              onReferenceScroll={() => {}} // No-op for overview
              showDismissButton={false}
              showChatButton={false}
            />
          );
        })}
      </div>

      {onSwitchToTriage && (
        <div style={{ marginTop: '8px', textAlign: 'center' }}>
          <TextButton onClick={onSwitchToTriage}>
            View All Claims in Triage
          </TextButton>
        </div>
      )}
    </div>
  );
};

export default OverviewPage;
